import {
  createContext,
  useContext,
  useMemo,
  FC,
  PropsWithChildren,
} from 'react'
import useMedia from 'use-media'

import { breakpoints } from 'styles/media'

type MediaQueriesMatches = {
  [key in keyof typeof breakpoints]: boolean
}

export const MediaQueryContext = createContext<MediaQueriesMatches>(null)

const mediaQueries = Object.fromEntries(
  Object.entries(breakpoints).map(([label, minWidth]) => [
    label,
    `(min-width: ${minWidth}px)`,
  ]),
) as { [key in keyof typeof breakpoints]: string }

export const MediaQueryProvider: FC<PropsWithChildren> = ({ children }) => {
  // Pass `false` as defaultState so the first client render matches SSR
  // (where window.matchMedia is unavailable and use-media returns false).
  // Without this, use-media reads window.matchMedia synchronously during the
  // initial useState call, producing a tree that diverges from SSR and trips
  // React 18 hydration errors. The library's own effect updates the state to
  // the real viewport value after mount.
  const sm = useMedia(mediaQueries.sm, false)
  const md = useMedia(mediaQueries.md, false)
  const lg = useMedia(mediaQueries.lg, false)
  const xl = useMedia(mediaQueries.xl, false)
  const xxl = useMedia(mediaQueries.xxl, false)
  const xxxl = useMedia(mediaQueries.xxxl, false)
  const xwide = useMedia(mediaQueries.xwide, false)

  const value = useMemo(
    () => ({
      sm,
      md,
      lg,
      xl,
      xxl,
      xxxl,
      xwide,
    }),
    [sm, md, lg, xl, xxl, xxxl, xwide],
  )

  return (
    <MediaQueryContext.Provider value={value}>
      {children}
    </MediaQueryContext.Provider>
  )
}

const defaultMockValues = {
  sm: true,
  md: true,
  lg: true,
  xl: true,
  xxl: true,
  xxxl: true,
  xwide: true,
} as const

export const mockValuesMobile: MediaQueriesMatches = {
  sm: true,
  md: false,
  lg: false,
  xl: false,
  xxl: false,
  xxxl: false,
  xwide: false,
} as const

export const MockMediaQueryProvider: FC<
  PropsWithChildren<{
    value?: MediaQueriesMatches
  }>
> = ({ value = defaultMockValues, children }) => {
  return (
    <MediaQueryContext.Provider value={value}>
      {children}
    </MediaQueryContext.Provider>
  )
}

export const useMediaQueryContext = () => useContext(MediaQueryContext)
